import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { DashboardTab } from "./generate/DashboardTab.js";
import { CreateTab } from "./generate/CreateTab.js";
import { ReviewsTab } from "./generate/ReviewsTab.js";
import { HistoryTab } from "./generate/HistoryTab.js";
import { MonitorTab } from "./generate/MonitorTab.js";
import { PersonasTab } from "./generate/PersonasTab.js";
import { PersonaReferenceUploadTab } from "./generate/PersonaReferenceUploadTab.js";
import { ManualTab } from "./generate/ManualTab.js";
import { useCurrentTenant } from "../hooks/useCurrentTenant.js";
import { OnboardingGate } from "../components/OnboardingGate.js";
import { CreateModeBar } from "../components/CreateModeBar.js";
import { ImageModeGate } from "../components/ImageModeGate.js";
import { CreateEntry } from "./generate/CreateEntry.js";
import { ImageModeRoutes } from "./generate/image/ImageModeRoutes.js";
import { ReelResearchTab } from "./generate/reel/ReelResearchTab.js";
import { ReRoutes } from "./generate/re/ReRoutes.js";
import { DeepResearchRoutes } from "./generate/deep-research/DeepResearchRoutes.js";
import { ImageBatchDetail } from "./generate/image/ImageBatchDetail.js";

function SubTabs() {
  const location = useLocation();
  // FIX-007: DASHBOARD is the first tab / default landing surface. PERSONAS
  // stays in the nav — it's the existing, load-bearing Persona/reference-image
  // setup surface (FIX-001's Manual documents it directly); the product
  // spec's own nav list didn't intend to remove it, only to add DASHBOARD.
  const tabs = [
    { path: "/generate/dashboard", label: "DASHBOARD" },
    { path: "/generate/create", label: "CREATE" },
    { path: "/generate/personas", label: "PERSONAS" },
    { path: "/generate/reviews", label: "REVIEWS" },
    { path: "/generate/history", label: "HISTORY" },
    { path: "/generate/monitor", label: "MONITOR" },
    { path: "/generate/manual", label: "MANUAL" },
  ];
  return (
    <div className="tabs">
      {tabs.map((tab) => (
        <a key={tab.path} href={tab.path} className={location.pathname.startsWith(tab.path) ? "active" : ""}>
          {tab.label}
        </a>
      ))}
    </div>
  );
}

export function GeneratePage() {
  const tenantId = useCurrentTenant();

  if (tenantId === null) {
    return (
      <div className="empty-state">
        You don't belong to a workspace yet. <a href="/workspaces/new">Create one</a> or ask a workspace ADMIN to invite you.
      </div>
    );
  }

  return (
    <div>
      <SubTabs />
      <Routes>
        {/* FIX-007: DASHBOARD is the default landing surface for GENERATE. */}
        <Route path="dashboard" element={<DashboardTab tenantId={tenantId} />} />
        {/* P2 remediation (GENERATE backlog follow-up review): onboarding
            gates ONLY the CREATE tab (the actual "GENERATE AVAILABLE" state)
            — DASHBOARD/PERSONAS/REVIEWS/HISTORY/MONITOR stay reachable
            unconditionally, since PERSONAS is exactly where the "upload your
            model" step sends an incomplete workspace; gating it too would
            create a catch-22 with no way to actually complete onboarding. */}
        {/* Image Mode (Phase B, 2026-09-19): CREATE now opens on a two-path entry — VIDEO or IMAGE, always both, for every authorized user. VIDEO renders the EXISTING gate + CreateTab completely unchanged (only a mode bar is added above it by this router); IMAGE opens the new still-image flow. */}
        <Route path="create" element={<CreateEntry />} />
        <Route
          path="create/video"
          element={
            <>
              <CreateModeBar mode="video" />
              <OnboardingGate tenantId={tenantId}>
                <CreateTab tenantId={tenantId} />
              </OnboardingGate>
            </>
          }
        />
        <Route
          path="create/image/*"
          element={
            <>
              <CreateModeBar mode="image" />
              <ImageModeGate tenantId={tenantId}>
                <ImageModeRoutes tenantId={tenantId} />
              </ImageModeGate>
            </>
          }
        />
        {/* FIX-049 stage 1: REEL (Research) - a third, isolated Create path; VIDEO and IMAGE above are untouched. */}
        <Route path="create/reel" element={<ReelResearchTab tenantId={tenantId} />} />
        {/* FIX-011 / RE stage 4: a fourth, isolated Create path (analysis -> review -> correction -> approval 1); the three above are untouched. */}
        <Route path="create/re/*" element={<ReRoutes tenantId={tenantId} />} />
        {/* FIX-062: DEEP RESEARCH (V4 port) - a fifth, isolated Create path; the four above are untouched. */}
        <Route path="create/deep-research/*" element={<DeepResearchRoutes tenantId={tenantId} />} />
        <Route path="personas" element={<PersonasTab tenantId={tenantId} />} />
        {/* Persona onboarding UX hardening (2026-09-12): a second, real, refreshable route — Create and Upload are two genuinely separate screens, not two sections of one page. */}
        <Route path="personas/upload" element={<PersonaReferenceUploadTab tenantId={tenantId} />} />
        <Route path="reviews" element={<ReviewsTab tenantId={tenantId} />} />
        {/* Image requests live in the SAME main REVIEW / HISTORY areas as Video: one request = one item; opening it shows every image. */}
        <Route path="reviews/image/:batchId" element={<ImageBatchDetail tenantId={tenantId} area="reviews" />} />
        <Route path="history/image/:batchId" element={<ImageBatchDetail tenantId={tenantId} area="history" />} />
        <Route path="history/*" element={<HistoryTab tenantId={tenantId} />} />
        <Route path="monitor" element={<MonitorTab tenantId={tenantId} />} />
        <Route path="manual" element={<ManualTab />} />
        <Route path="*" element={<Navigate to="/generate/dashboard" replace />} />
      </Routes>
    </div>
  );
}
